<template>
  <sui-segment-group style="max-width: 700px; margin: auto" raised>
    <sui-segment inverted style="text-align: center; font-size: 18px">
      Facturacion automatica
    </sui-segment>

    <sui-segment style="padding: 10px">
      <sui-form>
        <sui-form-field>
          <sui-label>Activar facturacion automatica</sui-label>
          <SuiCheckbox toggle v-model="form.enabled" />
        </sui-form-field>

        <sui-form-field>
          <sui-label>Tipo de horario</sui-label>
          <div class="ui input">
            <select v-model="form.schedule_type" class="ui dropdown">
              <option value="daily">Diario</option>
              <option value="custom">Personalizado (cron)</option>
            </select>
          </div>
        </sui-form-field>

        <sui-form-field>
          <sui-label>RFC emisor para facturacion automatica</sui-label>
          <div class="ui input">
            <select v-model.number="form.admin_rfc_id" class="ui dropdown">
              <option
                v-for="rfc in adminRfcOptions"
                :key="rfc.rfc_emiter_id"
                :value="rfc.rfc_emiter_id"
              >
                {{ formatRfcLabel(rfc) }}
              </option>
            </select>
          </div>
          <div class="help-text" v-if="!adminRfcOptions.length">
            No hay RFC emisor configurado.
          </div>
        </sui-form-field>

        <sui-form-field v-if="form.schedule_type === 'daily'">
          <sui-label>Hora de ejecucion (24h)</sui-label>
          <div class="time-row">
            <div class="ui input">
              <select v-model.number="form.hour" class="ui dropdown">
                <option v-for="hour in hours" :key="hour" :value="hour">
                  {{ formatNumber(hour) }}
                </option>
              </select>
            </div>
            <span class="time-separator">:</span>
            <div class="ui input">
              <select v-model.number="form.minute" class="ui dropdown">
                <option v-for="minute in minutes" :key="minute" :value="minute">
                  {{ formatNumber(minute) }}
                </option>
              </select>
            </div>
          </div>
        </sui-form-field>

        <sui-form-field v-else>
          <sui-label>Expresion cron</sui-label>
          <div class="ui input">
            <input
              type="text"
              placeholder="0 5 * * *"
              v-model="form.cron"
            />
          </div>
          <div class="help-text">Formato: minuto hora dia mes dia_semana</div>
        </sui-form-field>
      </sui-form>
    </sui-segment>

    <sui-segment>
      <sui-message error v-if="errorMessages.length">
        <template v-for="error in errorMessages" :key="error">
          <sui-message-item>{{ error }}</sui-message-item>
        </template>
      </sui-message>

      <sui-button @click="saveSettings" positive :class="{ loading: saving }">
        Guardar
      </sui-button>
      <sui-button
        @click="runNow"
        color="blue"
        :class="{ loading: running }"
      >
        Ejecutar ahora
      </sui-button>
    </sui-segment>
  </sui-segment-group>
</template>

<script setup>
import { onMounted, reactive, ref } from "vue";
import { useToast } from "vue-toastification";
import { SuiCheckbox } from "vue-fomantic-ui";
import API from "../../api/Api";

const toast = useToast();
const saving = ref(false);
const running = ref(false);
const errorMessages = ref([]);
const adminRfcOptions = ref([]);

const hours = Array.from({ length: 24 }, (_, index) => index);
const minutes = Array.from({ length: 60 }, (_, index) => index);

const form = reactive({
  enabled: false,
  schedule_type: "daily",
  hour: 5,
  minute: 0,
  cron: "",
  admin_rfc_id: null,
});

const formatNumber = (value) => String(value).padStart(2, "0");
const formatRfcLabel = (rfc) => {
  const legalName = rfc?.legal_name ? rfc.legal_name.trim() : "RFC";
  const emiter = rfc?.rfc_emiter ? rfc.rfc_emiter.trim() : "";
  return emiter ? `${legalName} (${emiter})` : legalName;
};

const validate = () => {
  errorMessages.value = [];
  if (form.schedule_type === "daily") {
    if (!Number.isInteger(form.hour) || form.hour < 0 || form.hour > 23) {
      errorMessages.value.push("La hora debe estar entre 0 y 23.");
    }
    if (!Number.isInteger(form.minute) || form.minute < 0 || form.minute > 59) {
      errorMessages.value.push("Los minutos deben estar entre 0 y 59.");
    }
  } else {
    const cron = form.cron ? form.cron.trim() : "";
    const parts = cron.split(/\s+/).filter(Boolean);
    if (cron.length === 0 || parts.length !== 5) {
      errorMessages.value.push("El cron debe tener 5 campos.");
    }
  }
  if (form.enabled && adminRfcOptions.value.length === 0) {
    errorMessages.value.push("No hay RFC emisor configurado.");
  } else if (form.enabled && adminRfcOptions.value.length > 0 && !form.admin_rfc_id) {
    errorMessages.value.push("Selecciona un RFC emisor.");
  }
  return errorMessages.value.length === 0;
};

onMounted(async () => {
  try {
    const [configResponse, rfcResponse] = await Promise.all([
      API.getAutoInvoiceConfig(),
      API.getClientRFC(null),
    ]);
    if (!rfcResponse.error && rfcResponse.data) {
      adminRfcOptions.value = rfcResponse.data.filter((rfc) => !rfc.deleted);
    }
    if (!configResponse.error && configResponse.data) {
      const data = configResponse.data;
      form.enabled = !!data.auto_invoice_enabled;
      form.schedule_type = data.auto_invoice_schedule_type || "daily";
      form.hour = Number.isInteger(data.auto_invoice_hour)
        ? data.auto_invoice_hour
        : 5;
      form.minute = Number.isInteger(data.auto_invoice_minute)
        ? data.auto_invoice_minute
        : 0;
      form.cron = data.auto_invoice_cron || "";
      form.admin_rfc_id = data.auto_invoice_admin_rfc_id || null;
    }
    if (!form.admin_rfc_id && adminRfcOptions.value.length > 0) {
      form.admin_rfc_id = adminRfcOptions.value[0].rfc_emiter_id;
    }
  } catch (error) {
    console.error(error);
  }
});

const saveSettings = async () => {
  if (!validate()) return;
  saving.value = true;
  try {
    await API.setAutoInvoiceConfig({
      enabled: form.enabled,
      schedule_type: form.schedule_type,
      hour: form.hour,
      minute: form.minute,
      cron: form.cron,
      admin_rfc_id: form.admin_rfc_id,
    });
    toast.success("Guardado con exito");
  } catch (error) {
    console.error(error);
  } finally {
    saving.value = false;
  }
};

const runNow = async () => {
  running.value = true;
  try {
    const response = await API.runAutoInvoiceNow();
    if (!response.error) {
      const processed = response.data?.processed ?? 0;
      toast.success(`Ejecucion iniciada. Facturas procesadas: ${processed}`);
    }
  } catch (error) {
    console.error(error);
  } finally {
    running.value = false;
  }
};
</script>

<style scoped>
.time-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.time-separator {
  font-weight: bold;
}

.help-text {
  margin-top: 6px;
  color: #6b7280;
  font-size: 12px;
}
</style>
